import React, { useState, useEffect, useRef } from "react";
import { Box, Text } from "@mantine/core";

export interface ContentBlockTypeMessageProps {
  /** Message text to display */
  message: string | null | undefined;

  /** Additional CSS class name for styling */
  className?: string;

  /** Theme configuration object */
  themeConfig?: any;
}

export const ContentBlockTypeMessage: React.FC<
  ContentBlockTypeMessageProps
> = ({ message, className, themeConfig }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState<number>(48); // Start with a large font size
  const [isCalculating, setIsCalculating] = useState(true);

  useEffect(() => {
    const calculateFontSize = () => {
      if (!containerRef.current || !textRef.current || !message) {
        setIsCalculating(false);
        return;
      }

      const container = containerRef.current;
      const textElement = textRef.current;

      // Get container dimensions (accounting for padding)
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Start with a large font size
      let currentFontSize = 72;
      const minFontSize = 12;
      const step = 2;

      // Binary search would be more efficient, but for simplicity, we'll use linear descent
      textElement.style.fontSize = `${currentFontSize}px`;

      // Keep reducing font size until text fits
      while (currentFontSize > minFontSize) {
        textElement.style.fontSize = `${currentFontSize}px`;

        const textWidth = textElement.scrollWidth;
        const textHeight = textElement.scrollHeight;

        // Check if text fits within container
        if (textWidth <= containerWidth && textHeight <= containerHeight) {
          break;
        }

        currentFontSize -= step;
      }

      setFontSize(currentFontSize);
      setIsCalculating(false);
    };

    // Reset calculation state
    setIsCalculating(true);

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateFontSize, 50);

    // Recalculate on window resize
    const handleResize = () => {
      setIsCalculating(true);
      clearTimeout(timeoutId);
      setTimeout(calculateFontSize, 50);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", handleResize);
    };
  }, [message]);

  return (
    <Box
      ref={containerRef}
      className={`content-block-message ${className || ""}`}
      h="100%"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--mantine-spacing-lg)",
        overflow: "hidden",
        ...(themeConfig?.contentBlocks?.message?.container && {
          ...themeConfig?.contentBlocks?.message?.container,
        }),
      }}
    >
      <Text
        ref={textRef}
        className="content-block-message__text"
        style={{
          fontSize: `${fontSize}px`,
          fontWeight: 600,
          textAlign: "center",
          lineHeight: 1.3,
          opacity: isCalculating ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
          wordBreak: "break-word",
          hyphens: "auto",
          maxWidth: "100%",
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          ...(themeConfig?.contentBlocks?.message?.text && {
            ...themeConfig?.contentBlocks?.message?.text,
          }),
        }}
      >
        {message}
      </Text>
    </Box>
  );
};
